import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PlaceIcon from '@material-ui/icons/Place';
import { RootState } from '../../../../../../redux/root';
import { loadDocumentsData } from '../DocumentsTable';
import ProgressBar from '../ProgressBar';
import FilesSection from './FilesSection';
import AvailableActions from './AvailableActions';
import ReviewComments from './ReviewComments/ReviewComments';
import { setOpenedTabIndex, setSelectedDocument } from '../../../../../../redux/slices/documentsSlice';
import { DocumentFileItem } from '../../../../types';
import SendButtonBar from '../SendButtonBar';

type Props = {
  onPermitItemSelected: Function;
};

const DocumentsItems: FC<Props> = ({ onPermitItemSelected }) => {
  const permitDocuments = useSelector((s: RootState) => s.documents.PermitDocuments);
  const OpenedTabIndex = useSelector((s: RootState) => s.documents.OpenedTabIndex);
  const vocabulary = useSelector((s: RootState) => s.general.vocabulary);
  const ref1 = useRef<HTMLDivElement>();
  const ref2 = useRef<HTMLDivElement>();
  const ref3 = useRef<HTMLDivElement>();
  const dispatch = useDispatch();
  const [showError, setShowError] = useState<boolean>(false);
  const [errorids, setErrorIds] = useState([]);
  const selectedDocument = useSelector((s: RootState) => s.documents.SelectedDocument);
  const selectedDocumentPermit = useSelector((s: RootState) => s.documents.SelectedDocumentPermit);
  const showDrawerContent = useSelector((s: RootState) => s.general.showDrawerContent);

  useEffect(() => {
    loadingDocData();
  }, [permitDocuments, selectedDocument]);

  useEffect(() => {
    if (ref1.current) ref1.current.scrollTop = 0;
    if (ref2.current) ref2.current.scrollTop = 0;
    if (ref3.current) ref3.current.scrollTop = 0;
  }, [OpenedTabIndex]);

  useEffect(() => {
    if (showDrawerContent) {
      dispatch(setSelectedDocument({} as DocumentFileItem));
      dispatch(setSelectedDocument({} as DocumentFileItem));
    }
  }, [showDrawerContent]);

  const loadingDocData = (forceLoad?: boolean) => {
    if (forceLoad || (permitDocuments.length === 0 && Object.keys(selectedDocument).length > 0)) {
      loadDocumentsData(selectedDocument, dispatch, vocabulary);
    }
  };

  return (
    <Root>
      <HeaderCard>
        <HeaderCol>
          <HeaderLabel>Project / Permit</HeaderLabel>
          <HeaderTitle title={selectedDocumentPermit?.namePermitName || ''}>
            {selectedDocumentPermit?.namePermitName || '—'}
          </HeaderTitle>
        </HeaderCol>
        <HeaderDivider />
        <HeaderCol>
          <HeaderLabel>Address</HeaderLabel>
          <HeaderAddress title={selectedDocumentPermit?.address || ''}>
            <PlaceIcon style={{ fontSize: 16 }} />
            <span>{selectedDocumentPermit?.address || '—'}</span>
          </HeaderAddress>
        </HeaderCol>
      </HeaderCard>

      <ProgressRow>
        <ProgressBar />
      </ProgressRow>

      <SendRow>
        <SendButtonBar setShowError={setShowError} setErrorIds={setErrorIds} />
      </SendRow>

      <MobileTabs>
        {[
          { idx: 1, label: 'Documents' },
          { idx: 2, label: 'Actions' },
          { idx: 3, label: 'Comments' },
        ].map(t => (
          <MobileTab key={t.idx} $active={OpenedTabIndex === t.idx} onClick={() => dispatch(setOpenedTabIndex(t.idx))}>
            {t.label}
          </MobileTab>
        ))}
      </MobileTabs>

      <ThreeColumns>
        <Section ref={ref1} $boxIndex={1} $currIndex={OpenedTabIndex} onClick={() => dispatch(setOpenedTabIndex(1))}>
          <SectionHeader>
            <SectionTitle>Documents</SectionTitle>
            <SectionCount>{permitDocuments.length}</SectionCount>
          </SectionHeader>
          <SectionBody>
            <FilesSection />
          </SectionBody>
        </Section>
        <Section ref={ref2} $boxIndex={2} $currIndex={OpenedTabIndex} onClick={() => dispatch(setOpenedTabIndex(2))}>
          <SectionHeader>
            <SectionTitle>Available Actions</SectionTitle>
          </SectionHeader>
          <SectionBody>
            <AvailableActions />
          </SectionBody>
        </Section>
        <Section ref={ref3} $boxIndex={3} $currIndex={OpenedTabIndex} onClick={() => dispatch(setOpenedTabIndex(3))}>
          <SectionHeader>
            <SectionTitle>Review Comments</SectionTitle>
          </SectionHeader>
          <SectionBody>
            <ReviewComments errorids={errorids} setErrorIds={setErrorIds} />
          </SectionBody>
        </Section>
      </ThreeColumns>
    </Root>
  );
};

export default DocumentsItems;

export const SubHeader = styled.div`
  color: var(--mainText);
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
  min-width: 0;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ProgressRow = styled.div`
  padding: 0;
`;

const SendRow = styled.div`
  padding: 0;
`;

const MobileTabs = styled.div`
  display: none;
  gap: 4px;
  padding: 4px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 840px) {
    display: flex;
  }
`;

const MobileTab = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 34px;
  border: none;
  border-radius: 7px;
  background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--neutral-0)' : 'var(--neutral-600)')};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
`;

const ThreeColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  min-width: 0;

  @media only screen and (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div<{ $boxIndex: number; $currIndex: number }>`
  display: flex;
  flex-direction: column;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  min-height: 420px;
  max-height: 56vh;

  @media only screen and (max-width: 840px) {
    display: ${({ $boxIndex, $currIndex }) => ($boxIndex === $currIndex ? 'flex' : 'none')};
    min-height: 380px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 16px;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--border);
`;

const SectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-600);
`;

const SectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-600);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.75rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const SectionBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 10px 12px;
`;

export const PaperWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 50px;
  overflow: auto;
  max-height: 90%;
  @media only screen and (max-width: 840px) {
    grid-template-columns: 1fr;
    margin: 10px;
    margin-top: 50px;
  }
`;
